/**
 * Lesson Service
 *
 * API service untuk Lesson Management System (Learn & Share)
 * Menangani semua operasi CRUD lessons dengan foto
 */

import apiClient from "../api/apiClient";

/**
 * Get all categories
 * @returns {Promise<Array>} List of categories with color
 */
export const getCategories = async () => {
  const response = await apiClient.get("/categories");
  return response.data || []; // Backend returns array directly, not {categories: [...]}
};

/**
 * Get all lessons with filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search keyword (NEW FEATURE)
 * @param {number} params.category_id - Filter by category
 * @param {string} params.project_id - Filter by project (Admin only)
 * @param {string} params.status - Filter by approval status
 * @param {string} params.sort_by - Sort order: 'newest' or 'oldest'
 * @returns {Promise<Object>} Paginated lessons data
 */
export const getLessons = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.category_id) queryParams.append("category_id", params.category_id);
  if (params.project_id) queryParams.append("project_id", params.project_id);
  if (params.status) queryParams.append("status", params.status);
  if (params.sort_by) queryParams.append("sort_by", params.sort_by);

  const response = await apiClient.get(`/lessons?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get lesson by ID
 * @param {string} id - Lesson UUID
 * @returns {Promise<Object>} Lesson detail
 */
export const getLessonById = async (id) => {
  const response = await apiClient.get(`/lessons/${id}`);
  return response.data;
};

/**
 * Create new lesson with photos
 * @param {Object} lessonData - Lesson form data
 * @param {Array<File>} photos - Photo files array
 * @returns {Promise<Object>} Created lesson
 */
export const createLesson = async (lessonData, photos = []) => {
  const formData = new FormData();

  // Add text fields
  formData.append("category_id", lessonData.category_id);
  formData.append("case", lessonData.case);
  formData.append("causes", lessonData.causes);
  formData.append("effect", lessonData.effect);
  formData.append("project_action", lessonData.project_action);
  formData.append(
    "future_recommendation",
    lessonData.future_recommendation,
  );

  // 🔥 WAJIB: kirim project_id jika ada
  if (lessonData.project_id) {
    formData.append("project_id", lessonData.project_id);
  }

  // Add photos
  photos.forEach((photo) => {
    formData.append("photos[]", photo);
  });

  const response = await apiClient.post("/lessons", formData);

  return response.data;
};

/**
 * Update lesson (NEW FEATURE)
 * Supports partial updates - only send fields you want to update
 * @param {string} id - Lesson UUID
 * @param {Object} lessonData - Fields to update (optional)
 * @param {Array<File>} photos - New photos to add (optional)
 * @param {Array<string>} deletedPhotos - Photo paths to delete (optional)
 * @returns {Promise<Object>} Updated lesson
 */
export const updateLesson = async (
  id,
  lessonData = {},
  photos = [],
  deletedPhotos = [],
) => {
  const formData = new FormData();

  // Add text fields (only if provided)
  if (lessonData.category_id)
    formData.append("category_id", lessonData.category_id);
  if (lessonData.case) formData.append("case", lessonData.case);
  if (lessonData.causes) formData.append("causes", lessonData.causes);
  if (lessonData.effect) formData.append("effect", lessonData.effect);
  if (lessonData.project_action)
    formData.append("project_action", lessonData.project_action);
  if (lessonData.future_recommendation)
    formData.append("future_recommendation", lessonData.future_recommendation);

  // Add new photos to existing lesson
  photos.forEach((photo) => {
    formData.append("photos[]", photo);
  });

  // Add deleted photo paths
  deletedPhotos.forEach((photoPath) => {
    formData.append("deleted_photos[]", photoPath);
  });

  // Don't set Content-Type manually - let axios set it with boundary
  const response = await apiClient.put(`/lessons/${id}`, formData);

  return response.data;
};

/**
 * Delete lesson
 * @param {string} id - Lesson UUID
 * @returns {Promise<Object>} Delete confirmation
 */
export const deleteLesson = async (id) => {
  const response = await apiClient.delete(`/lessons/${id}`);
  return response.data;
};

/**
 * Search lessons by keyword (NEW FEATURE)
 * Searches across: case_title, causes, effect, project_action, future_recommendation
 * @param {string} keyword - Search keyword
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Search results
 */
export const searchLessons = async (keyword, page = 1, limit = 10) => {
  const queryParams = new URLSearchParams({
    search: keyword,
    page: page.toString(),
    limit: limit.toString(),
  });

  const response = await apiClient.get(`/lessons?${queryParams.toString()}`);
  return response.data;
};
